import { CONFIG } from '../config';
import { localHeadline } from '../content/headlines';
import type { Scene } from '../content/types';
import { clamp } from '../core/math';
import { CopperGame, copperIn } from '../minigames/copper';
import { CornholeGame } from '../minigames/cornhole';
import { NoodlingGame, rollHole } from '../minigames/noodling';
import { StillGame } from '../minigames/still';
import type { MiniResult } from '../minigames/types';
import { copperSpec, cornholeSpec, noodleSpec, stillSpec, type StageEnv } from '../stages/backForty';
import type { Stage } from '../stages/types';
import { WorldGameStage } from '../stages/WorldGameStage';
import { usedToday, useToday } from '../state/daily';
import type { Interactions } from '../systems/Interactions';
import type { StageHost } from '../ui/StageHost';
import { SPOTS, type SpotId } from '../world/layout';
import type { Activities } from './Activities';
import type { RestCycle } from './RestCycle';
import type { GameServices } from './services';

const NOODLE_SPOTS: readonly SpotId[] = ['noodleA', 'noodleB', 'noodleC'];
const COPPER_SITES: ReadonlyArray<{ id: string; at: SpotId; name: string }> = [
  { id: 'condemned', at: 'condemnedDoor', name: 'the condemned trailer' },
  { id: 'abandonedA', at: 'abandonedADoor', name: 'Lot 14½' },
  { id: 'abandonedB', at: 'abandonedBDoor', name: 'the leaning trailer' },
];

/**
 * The Back Forty's ways to make (and lose) money: catfish noodling, stripping copper,
 * Uncle Rudy's still and cornhole against Lil' Ricky, plus poking around Lot 13.
 */
export class BackForty {
  constructor(
    private readonly s: GameServices,
    private readonly activities: Activities,
    private readonly rest: RestCycle,
    private readonly games: StageHost,
  ) {}

  private env(): StageEnv {
    const { world, player, rng, effects } = this.s;
    return {
      player,
      parent: world.dynamic,
      sparks: world.sparks,
      smoke: world.smoke,
      rng,
      sfx: (n) => this.s.sfx(n),
      shake: (i, sec) => effects.shake(i, sec),
    };
  }

  register(interactions: Interactions): void {
    const { store } = this.s;
    const add = interactions.add.bind(interactions);
    for (const id of NOODLE_SPOTS) {
      add({
        id: `noodle-${id}`,
        at: () => SPOTS[id],
        radius: 1.8,
        prompt: () =>
          store.doneToday(`noodle_${id}`) ? null : "Go noodlin' (stick your arm in a catfish hole)",
        action: () => this.noodle(id),
      });
    }
    for (const site of COPPER_SITES) {
      add({
        id: `copper-${site.id}`,
        at: () => SPOTS[site.at],
        radius: 1.8,
        prompt: () => (store.doneToday(`copper_${site.id}`) ? null : `Strip copper wire out of ${site.name}`),
        action: () => this.stripCopper(site.id),
      });
    }
    add({
      id: 'still',
      at: () => SPOTS.still,
      radius: 2.4,
      prompt: () =>
        usedToday(store, 'still_runs') >= CONFIG.stillRunsPerDay
          ? null
          : store.has('mash')
            ? "Fire up Uncle Rudy's still (-1 :corn:)"
            : "Uncle Rudy's still (needs :corn: Sweet Feed from Gas-N-Grub)",
      action: () => this.runStill(),
    });
    add({
      id: 'lot13',
      at: () => SPOTS.lot13Door,
      radius: 2.2,
      prompt: () => (store.doneToday('lot13') ? null : 'Poke around Lot 13'),
      action: () => this.lot13(),
    });
    add({
      id: 'cornhole-practice',
      at: () => SPOTS.cornholeA,
      radius: 1.8,
      prompt: () => 'Toss some bags (practice)',
      action: () => this.playCornhole(0),
    });
  }

  /** 0..1 for mini-games that get harder when you're hammered. */
  private get drunk(): number {
    return clamp((this.s.store.stat('buzz') - 50) / 50, 0, 1);
  }

  private ready(energy: number): boolean {
    const { store } = this.s;
    if (!this.activities.canAct() || this.games.isOpen) return false;
    if (store.stat('energy') < energy) {
      store.toast("You're too wiped out. Get some sleep or a Mtn Dewd.", 'bad', ':sleepy:');
      return false;
    }
    return true;
  }

  private face(spot: { x: number; z: number }): void {
    const p = this.s.player;
    p.facing = Math.atan2(spot.x - p.position.x, spot.z - p.position.z);
    p.syncTransform();
  }

  // --- catfish noodling ----------------------------------------------------------------

  noodle(id: SpotId): void {
    const { store, rng } = this.s;
    if (!this.ready(12) || store.doneToday(`noodle_${id}`)) return;
    store.markToday(`noodle_${id}`);
    store.incr('noodles');
    this.s.sfx('splash');
    this.face(SPOTS[id]);
    const game = new NoodlingGame(rng, this.drunk, [rollHole(rng)]);
    const stage = new WorldGameStage(noodleSpec(this.env(), SPOTS[id], game), this.s.player);
    this.games.open(stage, (_st: Stage, bailed): MiniResult => {
      store.apply({ stats: { energy: -12, hunger: -4 }, minutes: 30 });
      const o = game.outcome;
      if (bailed || !o)
        return {
          title: 'Chickened Out',
          art: ':chicken:',
          text: 'You pull your arm out before anything bites. Smart. Boring, but smart.',
        };
      const name = store.state.player.name;
      switch (o.kind) {
        case 'caught': {
          store.give('catfish', 1);
          store.incr('catfish');
          this.s.sfx('whoop');
          if (o.lbs > store.counter('catfishBest')) store.state.counters.catfishBest = o.lbs;
          if (o.lbs >= 30) {
            store.unlock('big_cat');
            store.headline(localHeadline('catfish', name));
            store.post({
              author: name,
              avatar: 'player',
              text: `${o.lbs} LB FLATHEAD :catfish::flex: caught him with my ARM. no I will not be taking questions about the arm`,
              likes: rng.int(60, 220),
            });
            store.apply({ clout: 10, money: 25 });
            return {
              title: 'Monster Cat!',
              art: ':catfish:',
              text: `A ${o.lbs}-pound flathead comes out of that hole attached to your forearm. Channel 6 pays you $25 for the picture. Possum Pete says it's "a decent one."`,
            };
          }
          store.addClout(3);
          return {
            title: "Got 'Em!",
            art: ':catfish:',
            text: `You wrestle a ${o.lbs}-pound channel cat out of the mud. Big Earl pays $${CONFIG.catfishPrice} for 'em, or fry it up on your grill.`,
          };
        }
        case 'snapper':
          store.apply({ stats: { health: -15 } });
          this.s.sfx('fail');
          return {
            title: 'SNAPPING TURTLE',
            art: ':turtle:',
            text: "That's not a catfish. That's a snapping turtle, and it has opinions. You still have all your fingers. You counted twice.",
          };
        case 'ripped':
          store.apply({ stats: { health: -6 } });
          return {
            title: 'Arm Slipped Out',
            art: ':flex:',
            text: `You yanked too hard. A ${o.lbs}-pounder rips loose and takes some of your forearm skin as a souvenir.`,
          };
        case 'escaped':
          return {
            title: 'It Got Away',
            art: ':wave:',
            text: `You went soft and a ${o.lbs}-pound catfish wriggled off into the murk. You'll tell people it was twice that.`,
          };
        case 'boot': {
          const cash = rng.int(5, 20);
          store.earn(cash);
          return {
            title: 'A Boot?',
            art: ':boot:',
            text: `You pull out a single cowboy boot. There's $${cash} stuffed in the toe. Finders keepers.`,
          };
        }
        default:
          return {
            title: 'Just Mud',
            art: ':mud-splat:',
            text: "Nothing in there but mud, a beer can from 1994 and the realization that you're elbow-deep in a creek.",
          };
      }
    });
  }

  // --- copper ---------------------------------------------------------------------------

  stripCopper(id: string): void {
    const { store, rng } = this.s;
    if (!this.ready(8) || store.doneToday(`copper_${id}`)) return;
    store.markToday(`copper_${id}`);
    this.s.sfx('clank');
    const maxLbs = copperIn(rng);
    const g = new CopperGame(rng, maxLbs);
    const site = COPPER_SITES.find((c) => c.id === id) ?? COPPER_SITES[0]!;
    const stage = new WorldGameStage(copperSpec(this.env(), SPOTS[site.at], g), this.s.player);
    this.games.open(stage, (_st: Stage, bailed): MiniResult => {
      store.apply({ stats: { energy: -8 }, minutes: 30 });
      const lbs = bailed ? 0 : Math.round(g.lbs);
      if (lbs > 0) {
        store.give('copper', lbs);
        store.incr('copperLbs', lbs);
      }
      if (g.snapped)
        return {
          title: 'SNAP',
          art: ':bolt:',
          text:
            lbs > 0
              ? `The wire snaps at a kink, but you salvage ${lbs} lb of copper. Junkyard Jerry pays by the pound.`
              : 'The wire snaps right away. You salvage nothing but a splinter.',
        };
      return {
        title: lbs > 0 ? 'Copper Get' : 'Nothing Worth Taking',
        art: ':copper-wire:',
        text:
          lbs > 0
            ? `You pull ${lbs} lb of clean copper out of the walls. Jerry's eyes are gonna light up.`
            : 'You poke at the walls for a while and leave with nothing. Somebody beat you to it.',
      };
    });
  }

  // --- moonshine ----------------------------------------------------------------------------

  runStill(): void {
    const { store, rng } = this.s;
    if (usedToday(store, 'still_runs') >= CONFIG.stillRunsPerDay) return;
    if (!store.has('mash')) {
      store.toast(
        'The still needs a sack of Sweet Feed mash. Gas-N-Grub sells it. For "horses."',
        'bad',
        ':corn:',
      );
      return;
    }
    if (!this.ready(10)) return;
    store.take('mash');
    useToday(store, 'still_runs');
    this.s.sfx('clank');
    const g = new StillGame(rng);
    const props = this.s.world.backForty;
    const stage = new WorldGameStage(
      stillSpec(
        this.env(),
        { at: SPOTS.still, fire: props.stillFire, setBusy: (busy) => (props.stillBusy = busy) },
        g,
      ),
      this.s.player,
    );
    this.games.open(stage, (_st: Stage, bailed): MiniResult => {
      store.apply({ stats: { energy: -10 }, minutes: 60 });
      if (g.boom) {
        store.incr('stillBooms');
        store.unlock('kaboom');
        store.apply({ stats: { health: -20 } });
        store.headline(localHeadline('still', store.state.player.name));
        this.s.sfx('boom');
        this.s.effects.shake(0.8, 0.6);
        return {
          title: 'KA-BOOM',
          art: ':boom:',
          text: 'The pressure gets away from you. The cap blows off, a fireball rolls up the holler, and your eyebrows are now a memory. Uncle Rudy would be proud.',
        };
      }
      const jars = bailed ? 0 : g.jars;
      if (jars > 0) {
        store.give('shine', jars);
        store.incr('shineJars', jars);
      }
      return {
        title: jars > 0 ? 'White Lightning' : 'Nothing But Warm Mash',
        art: ':moonshine:',
        text:
          jars > 0
            ? `You fill ${jars} mason jar${jars > 1 ? 's' : ''} with crystal-clear shine. Trenchcoat Terry buys it after dark at the burn barrels.`
            : "The fire never got hot enough. You're left with a pot of warm, sad mash.",
      };
    });
  }

  drinkShine(): void {
    const { store } = this.s;
    if (!this.activities.canAct()) return;
    if (!store.take('shine')) return;
    store.incr('shineDrunk');
    this.s.sfx('gulp');
    this.s.player.doBusy({
      pose: 'drink',
      until: this.s.now() + 1.8,
      onDone: () => {
        store.apply({ stats: { buzz: 38, energy: 10, health: -4 } });
        this.s.ui.hud.float(':fire: WHOOOO', this.s.player.headPosition(), '#ffb02e');
        if (store.stat('buzz') >= 100) this.rest.passOut('blackout');
      },
    });
  }

  // --- cornhole -----------------------------------------------------------------------------

  /** A game against Lil' Ricky. `bet` 0 is free practice. */
  playCornhole(bet: number): void {
    const { store, rng } = this.s;
    if (this.games.isOpen) return;
    if (bet > 0) {
      if (usedToday(store, 'cornhole') >= CONFIG.cornholeGamesPerDay) {
        store.toast('Ricky: "I got a shift at the Gas-N-Grub. Tomorrow, chief."', 'info', ':corn:');
        return;
      }
      if (!store.spend(bet)) {
        store.toast(`You need $${bet} to bet.`, 'bad', ':cash:');
        return;
      }
      useToday(store, 'cornhole');
      store.incr('cornholeGames');
    }
    const g = new CornholeGame(rng, this.drunk);
    const stage = new WorldGameStage(
      cornholeSpec(
        this.env(),
        { from: SPOTS.cornholeA, target: SPOTS.cornholeB, ricky: this.s.npcs.get('ricky') ?? null },
        g,
      ),
      this.s.player,
    );
    this.games.open(stage, (_st: Stage, bailed): MiniResult => {
      store.apply({ minutes: 30, stats: { energy: -4 } });
      const w = bailed ? 'ricky' : g.winner;
      const score = `${g.score.you}-${g.score.ricky}`;
      if (w === 'you') {
        store.incr('cornholeWins');
        store.unlock('cornhole_champ');
        if (bet > 0) store.earn(bet * 2);
        store.addClout(bet > 0 ? 4 : 1);
        this.s.sfx('airhorn');
        return {
          title: 'CORNHOLE CHAMP',
          art: ':trophy:',
          text:
            bet > 0
              ? `You beat Lil' Ricky ${score} and take his $${bet}. He says the wind was blowing. There is no wind.`
              : `You win ${score}. Ricky, watching from the picnic table, pretends he wasn't.`,
        };
      }
      if (w === 'tie') {
        if (bet > 0) store.earn(bet);
        return {
          title: 'Push',
          art: ':handshake:',
          text: `${score}. Nobody wins, everybody keeps their money, everybody's mad.`,
        };
      }
      return {
        title: bailed ? 'Forfeit' : 'Ricky Wins',
        art: ':corn:',
        text:
          bet > 0
            ? `Ricky wins ${score} and pockets your $${bet}. He does a little dance. You'll get him next time.`
            : `Practice makes... well. ${score}.`,
      };
    });
  }

  // --- Lot 13 -------------------------------------------------------------------------------

  lot13(): void {
    const { store, rng, ui } = this.s;
    if (!this.activities.canAct() || store.doneToday('lot13')) return;
    store.markToday('lot13');
    store.incr('lot13');
    this.face(SPOTS.lot13);
    const night = store.minutes >= 21 * 60 || store.minutes < 5 * 60;
    if (night) store.unlock('lot13');
    const roll = rng.next();
    let scene: Scene;
    if (roll < 0.3) {
      const cash = rng.int(15, 40);
      store.earn(cash);
      scene = {
        title: 'Lot 13',
        art: ':jar:',
        text: `Under the charred floorboards: a coffee can with $${cash} in it and a note that says "FOR BAIL." Whoever needed it isn't coming back for it.`,
        choices: [],
      };
    } else if (roll < 0.55) {
      store.give('copper', 2);
      store.incr('copperLbs', 2);
      scene = {
        title: 'Lot 13',
        art: ':copper-wire:',
        text: "A coil of copper wire somebody stashed under the hitch. 2 lb. Nobody's coming back for that either.",
        choices: [],
      };
    } else if (roll < 0.8 || !night) {
      store.apply({ stats: { energy: -8 } });
      scene = {
        title: 'Lot 13',
        art: ':chair:',
        text: "The rocking chair on the lot starts rocking. There's no wind. You leave at a speed you didn't know you had.",
        choices: [],
      };
    } else {
      store.apply({ clout: 6 });
      scene = {
        title: 'Lot 13',
        art: ':ghost:',
        text: "Something pale moves in the burned-out shell. It's Possum Pete, in his long johns, looking for his teeth. You help. You find them. You will never speak of this.",
        choices: [],
      };
    }
    ui.modal.show(scene);
    ui.hud.float(':eyes:', this.s.player.headPosition(), '#ffffff');
  }
}
